import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { SEO } from "../components/SEO";
import { api } from "../lib/api";
import { Clock, User, ArrowRight, Search } from "lucide-react";
import { Input } from "../components/ui/input";

export default function BlogPage() {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");
  const [allPosts, setAllPosts] = useState([]);

  useEffect(() => {
    api.getBlogPosts().then(setAllPosts).catch(console.error);
  }, []);

  const categories = ["All", ...new Set(allPosts.map(p => p.category))];

  const filtered = allPosts.filter(post => {
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <div>
      <SEO 
        title="Blog - Immigration Insights & Expert Tips"
        description="Stay updated with the latest immigration news, visa guides, study abroad tips, and expert advice from NexFLY Immigration Consultancy."
        keywords="immigration blog, visa tips, study abroad guide, Canada PR, work permit guide, IELTS tips, immigration news"
      />
      {/* Hero */}
      <section className="relative py-20 bg-white overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-0 w-[40%] h-full bg-brand-50/50 rounded-br-[100px]" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <Badge className="bg-brand-100 text-brand-700 hover:bg-brand-100 mb-6 px-4 py-1.5 text-xs font-semibold rounded-full">
              NexFLY Blog
            </Badge>
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 leading-tight mb-5">
              Immigration Insights
              <span className="text-brand-500 block">& Expert Tips</span>
            </h1>
            <p className="text-lg text-gray-500 leading-relaxed">
              Stay updated with the latest immigration news, visa guides, and expert advice.
            </p>
          </div>
        </div>
      </section>

      {/* Filters */}
      <section className="py-10 bg-gray-50/50 border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex flex-wrap gap-2">
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={selectedCategory === cat ? "default" : "outline"}
                  onClick={() => setSelectedCategory(cat)}
                  className={`rounded-xl text-xs h-9 px-4 transition-all duration-200 ${
                    selectedCategory === cat
                      ? "bg-brand-500 hover:bg-brand-700 text-white shadow-sm"
                      : "border-gray-200 text-gray-600 hover:bg-brand-50 hover:text-brand-500 hover:border-brand-200"
                  }`}
                >
                  {cat}
                </Button>
              ))}
            </div>
            <div className="relative w-full sm:w-64">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 rounded-xl h-10 border-gray-200 focus:border-brand-300 focus:ring-brand-200"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Blog Grid */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {filtered.length === 0 ? (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">No articles found matching your criteria.</p>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-7">
              {filtered.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`}>
                  <Card className="border-0 shadow-sm bg-white rounded-2xl hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group overflow-hidden h-full cursor-pointer">
                    <div className="relative overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-52 object-cover group-hover:scale-105 transition-transform duration-500"
                      />
                      <Badge className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm text-brand-700 text-xs font-semibold rounded-lg">
                        {post.category}
                      </Badge>
                    </div>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4 text-xs text-gray-400 mb-3">
                        <span className="flex items-center gap-1">
                          <User className="w-3 h-3" /> {post.author}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="w-3 h-3" /> {post.readTime}
                        </span>
                      </div>
                      <h3 className="font-semibold text-gray-900 text-base mb-2 group-hover:text-brand-700 transition-colors leading-snug">
                        {post.title}
                      </h3>
                      <p className="text-gray-500 text-sm leading-relaxed mb-4 line-clamp-2">
                        {post.excerpt}
                      </p>
                      <span className="text-brand-500 text-sm font-medium flex items-center gap-1 group-hover:gap-2 transition-all">
                        Read More <ArrowRight className="w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
